import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';
import { ShelfLocations } from '../components/warehouse/ShelfLocations';

interface ShelvingRow {
  supplierOrderLineId: string;
  productId: string;
  sku: string;
  name: string;
  brand: string | null;
  quantityReceived: number;
  orderLineId: string | null;
  orderNumber: string | null;
  destination: 'STOCK' | 'CUSTOMER_ORDER';
  poNumber: string;
  receivedAt: string;
  mainLocation: string | null;
}

interface Part {
  productId: string;
  sku: string;
  name: string;
  brand: string | null;
  manufacturerRef: string | null;
  ean: string | null;
  ean2: string | null;
  active: boolean;
  stock: number;
  /** Main first; empty = location unknown. */
  locations: string[];
}

type PutAwayResult = 'CONFIRMED' | 'RECORDED' | 'ADDED' | 'REPLACED' | 'CHOICE_REQUIRED';

/** Same rule as the server (ShelfLocationService.parseLocationCode): A–F, two digits, one digit. */
const LOCATION_RE = /^(LOC-)?[A-F]-\d{2}-\d$/i;
const isShelfLabel = (s: string) => s.trim().toUpperCase().startsWith('LOC-');

/**
 * Warehouse locations V1 (migration 144) — ONE action: scan the part, then the shelf.
 *
 * Put-away after a restock, mapping existing stock aisle by aisle, moving stock and putting a
 * customer return back on the shelf are all this screen. The server decides what the second scan
 * means: a confirmation (already there), a first location (recorded), or — when the part already
 * lives elsewhere — the operator's choice between "Add" (it overflows) and "Replace" (it moved, or
 * the record was wrong). No quantity per shelf, and no stock movement (Invariant #27).
 *
 * A barcode the system does not know is never a dead end: find the product by its reference and
 * link the code to it, so the next scan is recognised.
 *
 * The scanner is a keyboard wedge: it types into the focused field and presses Enter, so each
 * step is one autofocused field.
 */
export default function Shelving() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  /* `?po=<id>` — arrived here straight from validating that reception. The list narrows to that
     pallet so the operator shelves what they are standing next to, not the last month's. */
  const [params, setParams] = useSearchParams();
  const poId = params.get('po');

  const [rows, setRows] = useState<ShelvingRow[]>([]);
  const [scan, setScan] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Step 1 outcomes.
  const [unknownCode, setUnknownCode] = useState<string | null>(null);
  const [matches, setMatches] = useState<Part[]>([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Part[] | null>(null);
  // Step 2.
  const [part, setPart] = useState<Part | null>(null);
  const [manual, setManual] = useState('');
  const [choice, setChoice] = useState<{ location: string; manual: boolean } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const { data } = await apiClient.get<ShelvingRow[]>(
        `/warehouse/shelving${poId ? `?poId=${encodeURIComponent(poId)}` : ''}`
      );
      setRows(data);
    } catch (err) {
      showApiError(err);
    }
  }, [poId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [part, unknownCode, matches, choice]);

  const reset = (): void => {
    setScan('');
    setError(null);
    setUnknownCode(null);
    setMatches([]);
    setQuery('');
    setResults(null);
    setPart(null);
    setManual('');
    setChoice(null);
  };

  const choosePart = (p: Part): void => {
    setPart(p);
    setUnknownCode(null);
    setMatches([]);
    setResults(null);
    setError(null);
    setScan('');
  };

  /* ── step 1: the part ── */
  const scanPart = async (raw: string): Promise<void> => {
    const code = raw.trim();
    setScan('');
    if (!code) return;
    setError(null);
    if (isShelfLabel(code)) {
      setError(t('shelving.notAShelf'));
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.get<{ code: string; matches: Part[] }>(
        `/warehouse/locations/part?code=${encodeURIComponent(code)}`
      );
      if (data.matches.length === 1) choosePart(data.matches[0]);
      else if (data.matches.length > 1) setMatches(data.matches);
      else {
        setUnknownCode(code);
        setResults(null);
      }
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const pickFromList = async (productId: string): Promise<void> => {
    reset();
    try {
      const { data } = await apiClient.get<{ matches: Part[] }>(
        `/warehouse/locations/part?productId=${encodeURIComponent(productId)}`
      );
      if (data.matches[0]) choosePart(data.matches[0]);
    } catch (err) {
      showApiError(err);
    }
  };

  const search = async (): Promise<void> => {
    if (query.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await apiClient.get<Part[]>(
        `/warehouse/locations/search?q=${encodeURIComponent(query.trim())}`
      );
      setResults(data);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const linkAndUse = async (p: Part): Promise<void> => {
    if (!unknownCode) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post<Part>('/warehouse/locations/link-barcode', {
        productId: p.productId,
        code: unknownCode,
      });
      push({
        tone: 'success',
        message: t('shelving.linked', { code: unknownCode, sku: data.sku }),
      });
      choosePart(data);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  /* ── step 2: the shelf ── */
  const putAway = async (
    location: string,
    opts: { manual: boolean; mode?: 'ADD' | 'REPLACE' }
  ): Promise<void> => {
    if (!part) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await apiClient.post<{
        result: PutAwayResult;
        location: string;
        locations: string[];
      }>('/warehouse/locations/put-away', {
        productId: part.productId,
        location,
        manual: opts.manual || undefined,
        mode: opts.mode,
      });
      if (data.result === 'CHOICE_REQUIRED') {
        setChoice({ location, manual: opts.manual });
        return;
      }
      push({
        tone: 'success',
        message: t(`shelving.result.${data.result}`, { sku: part.sku, location: data.location }),
      });
      reset();
      void load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const scanShelf = (raw: string): void => {
    const code = raw.trim();
    setScan('');
    if (!code) return;
    if (!isShelfLabel(code)) {
      // A part barcode scanned at the shelf step: say so, keep the part.
      setError(t('shelving.notAShelf'));
      return;
    }
    void putAway(code, { manual: false });
  };

  const submitManual = (): void => {
    if (!LOCATION_RE.test(manual.trim())) {
      setError(t('shelving.manualFormat'));
      return;
    }
    void putAway(manual.trim(), { manual: true });
  };

  const toPutAway = rows.filter((r) => r.destination === 'STOCK');
  const staying = rows.filter((r) => r.destination === 'CUSTOMER_ORDER');
  const choiceLabel = choice ? choice.location.toUpperCase().replace(/^LOC-/, '') : '';

  return (
    <div>
      <PageHeader title={t('shelving.title')} description={t('shelving.description')} />

      <section className="mb-6 rounded-lg border bg-card p-4">
        {!part ? (
          <>
            <h2 className="mb-2 text-sm font-semibold">{t('shelving.stepPart')}</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void scanPart(scan);
              }}
            >
              <Input
                ref={inputRef}
                autoFocus
                value={scan}
                disabled={busy}
                onChange={(e) => setScan(e.target.value)}
                placeholder={t('shelving.partPlaceholder')}
                className="h-12 font-mono text-lg"
              />
            </form>

            {matches.length > 1 && (
              <div className="mt-3">
                <p className="mb-2 text-sm">{t('shelving.severalMatches')}</p>
                <PartList parts={matches} action={t('shelving.pick')} onPick={choosePart} />
              </div>
            )}

            {unknownCode && (
              <div className="mt-3 rounded-md border border-warning/40 bg-warning/5 p-3">
                <p className="mb-2 text-sm">{t('shelving.unknownCode', { code: unknownCode })}</p>
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void search();
                  }}
                >
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('shelving.searchPlaceholder')}
                  />
                  <Button type="submit" disabled={busy || query.trim().length < 2}>
                    {t('shelving.search')}
                  </Button>
                </form>
                {results && results.length === 0 && (
                  <p className="mt-2 text-sm text-muted-foreground">{t('shelving.noResult')}</p>
                )}
                {results && results.length > 0 && (
                  <div className="mt-2">
                    <PartList
                      parts={results}
                      action={t('shelving.linkAndUse', { code: unknownCode })}
                      onPick={(p) => void linkAndUse(p)}
                      secondary={{ label: t('shelving.useWithoutLink'), onPick: choosePart }}
                      disabled={busy}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate font-medium">{part.name}</div>
                <div className="text-xs text-muted-foreground">
                  {[part.sku, part.brand, part.manufacturerRef].filter(Boolean).join(' · ')} ·{' '}
                  {t('shelving.stock', { qty: part.stock })}
                </div>
                <div className="mt-2 text-sm">
                  {part.locations.length === 0 ? (
                    <span className="font-medium text-warning">
                      {t('shelving.locationUnknown')}
                    </span>
                  ) : (
                    <>
                      <span className="font-semibold">
                        {t('shelving.mainLocation', { location: part.locations[0] })}
                      </span>
                      {part.locations.length > 1 && (
                        <span className="ml-2 text-muted-foreground">
                          {t('shelving.otherLocations', {
                            locations: part.locations.slice(1).join(', '),
                          })}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={reset}>
                {t('shelving.cancel')}
              </Button>
            </div>

            {choice ? (
              <div className="rounded-md border border-primary/40 bg-primary/5 p-3">
                <p className="mb-3 text-sm">
                  {t('shelving.choiceTitle', {
                    sku: part.sku,
                    locations: part.locations.join(', '),
                    location: choiceLabel,
                  })}
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Button
                    size="lg"
                    disabled={busy}
                    onClick={() =>
                      void putAway(choice.location, { manual: choice.manual, mode: 'ADD' })
                    }
                  >
                    {t('shelving.add')}
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    disabled={busy}
                    onClick={() =>
                      void putAway(choice.location, { manual: choice.manual, mode: 'REPLACE' })
                    }
                  >
                    {t('shelving.replace')}
                  </Button>
                  <p className="text-xs text-muted-foreground">{t('shelving.addHint')}</p>
                  <p className="text-xs text-muted-foreground">{t('shelving.replaceHint')}</p>
                </div>
              </div>
            ) : (
              <>
                <h2 className="mb-2 text-sm font-semibold">{t('shelving.stepShelf')}</h2>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    scanShelf(scan);
                  }}
                >
                  <Input
                    ref={inputRef}
                    autoFocus
                    value={scan}
                    disabled={busy}
                    onChange={(e) => setScan(e.target.value)}
                    placeholder={t('shelving.shelfPlaceholder')}
                    className="h-12 font-mono text-lg"
                  />
                </form>
                <form
                  className="mt-3 flex flex-wrap items-end gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    submitManual();
                  }}
                >
                  <div>
                    <label
                      className="mb-1 block text-xs text-muted-foreground"
                      htmlFor="manual-loc"
                    >
                      {t('shelving.manualLabel')}
                    </label>
                    <Input
                      id="manual-loc"
                      value={manual}
                      onChange={(e) => setManual(e.target.value)}
                      placeholder={t('shelving.manualPlaceholder')}
                      className="w-32 font-mono uppercase"
                    />
                  </div>
                  <Button type="submit" variant="outline" disabled={busy || !manual.trim()}>
                    {t('shelving.manualSubmit')}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {t('shelving.manualFormat')}
                  </span>
                </form>
              </>
            )}
          </>
        )}
        {error && <p className="mt-2 text-sm font-medium text-destructive">{error}</p>}
      </section>

      {poId && (
        <p className="mb-3 flex items-center gap-2 text-sm">
          <Badge variant="secondary">
            {t('shelving.filteredByPo', { po: rows[0]?.poNumber ?? '…' })}
          </Badge>
          <button
            type="button"
            className="text-xs text-primary underline"
            onClick={() => setParams({})}
          >
            {t('shelving.showAll')}
          </button>
        </p>
      )}

      <Group
        title={t('shelving.toPutAway')}
        hint={t('shelving.toPutAwayHint')}
        count={toPutAway.length}
      >
        {toPutAway.map((r) => (
          <li key={r.supplierOrderLineId}>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40"
              onClick={() => void pickFromList(r.productId)}
            >
              <div className="min-w-0">
                <div className="truncate font-medium">{r.name}</div>
                <div className="text-xs text-muted-foreground">
                  {r.sku} · {r.poNumber} · ×{r.quantityReceived}
                </div>
              </div>
              <ShelfLocations
                productId={r.productId}
                locations={r.mainLocation ? [r.mainLocation] : []}
              />
            </button>
          </li>
        ))}
      </Group>

      <Group title={t('shelving.stayingInR')} count={staying.length}>
        {staying.map((r) => (
          <li
            key={r.supplierOrderLineId}
            className="flex items-center justify-between gap-3 px-4 py-3"
          >
            <div className="min-w-0">
              <div className="truncate font-medium">{r.name}</div>
              <div className="text-xs text-muted-foreground">
                {r.sku} · {r.orderNumber ?? '—'} · ×{r.quantityReceived}
              </div>
            </div>
            <Badge variant="warning">{t('shelving.reservedForOrder')}</Badge>
          </li>
        ))}
      </Group>
    </div>
  );
}

function PartList({
  parts,
  action,
  onPick,
  secondary,
  disabled,
}: {
  parts: Part[];
  action: string;
  onPick: (p: Part) => void;
  secondary?: { label: string; onPick: (p: Part) => void };
  disabled?: boolean;
}) {
  return (
    <ul className="divide-y rounded-md border bg-background">
      {parts.map((p) => (
        <li
          key={p.productId}
          className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
        >
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{p.name}</div>
            <div className="text-xs text-muted-foreground">
              {[p.sku, p.brand, p.manufacturerRef, p.ean, p.ean2].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div className="flex gap-2">
            {secondary && (
              <Button
                size="sm"
                variant="ghost"
                disabled={disabled}
                onClick={() => secondary.onPick(p)}
              >
                {secondary.label}
              </Button>
            )}
            <Button size="sm" disabled={disabled} onClick={() => onPick(p)}>
              {action}
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Group({
  title,
  hint,
  count,
  children,
}: {
  title: string;
  hint?: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-4 overflow-hidden rounded-lg border">
      <header className="flex items-center justify-between gap-2 border-b bg-muted/40 px-4 py-2">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        <Badge variant="muted">{count}</Badge>
      </header>
      <ul className="divide-y">
        {count === 0 ? (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">—</li>
        ) : (
          children
        )}
      </ul>
    </section>
  );
}
